import { ArrowSquareOut } from "@phosphor-icons/react";
import { Button } from "@replicate/ui";
import { match } from "ts-pattern";
import { formatCurrency } from "../intl_helpers";
import type { Account } from "../types";
import { route } from "../urls";
import GlyphLoader from "./glyph-loader";
import IconTooltip from "./icon-tooltip";
import { useInvoicesQuery, useUnusedCreditQuery } from "./invoices";

function ValueDisplay({
  title,
  meta,
  children,
  warn = false,
}: {
  title: string | React.ReactNode;
  meta?: React.ReactNode;
  children: React.ReactNode;
  warn?: boolean;
}) {
  return (
    <div
      className={`${
        warn ? "bg-r8-red-2 text-r8-red-10" : ""
      } flex flex-col items-center justify-center flex-1 p-3 gap-0.5`}
    >
      <div className="flex items-center justify-center gap-x-1 text-r8-xs flex-wrap text-center min-h-8">
        <div className="text-r8-gray-11">{title}</div>
        {meta}
      </div>
      <div className="flex items-center justify-center gap-1 flex-wrap">
        <div className="text-r8-2xl font-heading font-semibold">{children}</div>
      </div>
    </div>
  );
}

export default function BillingSummary({
  account,
  alwaysShowCredit,
  linkToInvoices,
}: {
  account: Account;
  alwaysShowCredit: boolean;
  linkToInvoices: boolean;
}) {
  const invoiceQuery = useInvoicesQuery(account);
  const unusedCreditQuery = useUnusedCreditQuery(account);

  const latestInvoice = match(invoiceQuery)
    .with(
      { status: "success" },
      ({ data: { invoices } }) =>
        // Invoices are sorted in reverse chronological order.
        invoices.filter((invoice) => invoice.type === "monthly-usage")[0]
    )
    .otherwise(() => null);

  const currentInvoice =
    latestInvoice &&
    (!latestInvoice.ended_before ||
      Date.parse(latestInvoice.ended_before) > Date.now())
      ? latestInvoice
      : null;

  const outstandingBalance = Number.parseFloat(
    currentInvoice?.total_cost || "0"
  );

  return (
    <div className="flex flex-col sm:flex-row flex-wrap lg:flex-nowrap justify-between border border-r8-gray-6 bg-white dark:bg-r8-gray-1 min-h-24 items-stretch divide-y sm:divide-y-0 sm:divide-x">
      {match(invoiceQuery)
        .with({ status: "error" }, () => (
          <div className="flex items-center justify-center flex-1 p-3">
            <p>Unable to fetch usage</p>
          </div>
        ))
        .with({ status: "success" }, () => (
          <>
            <ValueDisplay
              title={
                <div className="flex items-center gap-1">
                  <p className="max-w-32">
                    Outstanding balance this month{" "}
                    <IconTooltip
                      icon="Question"
                      weight="light"
                      tooltipText={
                        outstandingBalance > 0
                          ? `Your usage this month has exceeded your credit. If you do not purchase more credit, we will bill your default payment method for ${formatCurrency(
                              outstandingBalance
                            )} at the start of next month.`
                          : "If your usage exceeds your credit, we will bill your default payment method for any excess at the start of the next month."
                      }
                      anchorClass="flex-shrink-0 inline"
                    />
                  </p>
                </div>
              }
              warn={outstandingBalance > 0}
            >
              {formatCurrency(outstandingBalance)}
            </ValueDisplay>
            <ValueDisplay
              title={<div className="max-w-20">Usage this month</div>}
            >
              {formatCurrency(
                Number.parseFloat(
                  currentInvoice?.total_cost_before_adjustments || "0"
                )
              )}
            </ValueDisplay>
          </>
        ))
        .with({ status: "pending" }, () => (
          <div className="flex items-center justify-center flex-1 p-3 gap-3">
            <GlyphLoader />
            <p>Loading usage...</p>
          </div>
        ))
        .exhaustive()}

      {alwaysShowCredit
        ? match(unusedCreditQuery)
            .with({ status: "error" }, () => (
              <div className="flex items-center justify-center flex-1 p-3">
                <p>Unable to fetch credit</p>
              </div>
            ))
            .with(
              { status: "success" },
              ({ data: { link_to_add_credit, unused_credit } }) => {
                const unusedCredit = Number.parseFloat(unused_credit || "0");
                return (
                  <ValueDisplay
                    title={
                      <div className="max-w-24">
                        Credit remaining{" "}
                        {link_to_add_credit ? (
                          <a
                            className="flex-shrink-0"
                            href={link_to_add_credit}
                          >
                            (Add more)
                          </a>
                        ) : null}
                      </div>
                    }
                  >
                    {formatCurrency(unusedCredit)}
                  </ValueDisplay>
                );
              }
            )
            .with({ status: "pending" }, () => (
              <div className="flex items-center justify-center flex-1 p-3 gap-3">
                <GlyphLoader />
                <p>Loading credit...</p>
              </div>
            ))
            .exhaustive()
        : match(unusedCreditQuery)
            .with({ status: "success" }, ({ data: { unused_credit } }) => {
              const unusedCredit = Number.parseFloat(unused_credit || "0");

              if (unusedCredit <= 0) {
                return null;
              }

              return (
                <ValueDisplay
                  title={<div className="max-w-20">Credit remaining</div>}
                >
                  {formatCurrency(unusedCredit)}
                </ValueDisplay>
              );
            })
            .otherwise(() => null)}

      {linkToInvoices ? (
        <div className="flex flex-col gap-2 p-3 flex-1 justify-center md:max-w-64">
          <div className="flex items-stretch justify-stretch lg:justify-center [&>*]:w-full [&>*]:sm:flex-none">
            <Button
              size="sm"
              endIcon={<ArrowSquareOut />}
              disabled={!latestInvoice}
              render={
                latestInvoice?.id ? (
                  // biome-ignore lint/a11y/useAnchorContent: rendered by Button
                  <a
                    href={
                      account.kind === "organization"
                        ? route("organization_invoice_detail", {
                            organization_name: account.username,
                            invoice_id: latestInvoice.id,
                          })
                        : route("user_invoice_detail", {
                            username: account.username,
                            invoice_id: latestInvoice.id,
                          })
                    }
                    target="_blank"
                    rel="noreferrer"
                  />
                ) : (
                  <div />
                )
              }
            >
              <span className="whitespace-nowrap">Latest invoice</span>
            </Button>
          </div>

          <div className="flex items-stretch justify-stretch lg:justify-center [&>*]:w-full [&>*]:sm:flex-none">
            <Button
              variant="outlined"
              size="sm"
              render={
                // biome-ignore lint/a11y/useAnchorContent: rendered by Button
                <a href={route("account_billing_settings")} />
              }
            >
              <span className="whitespace-nowrap">Manage billing</span>
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
